import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { computeLineDiff } from "lib/lineDiff";
import {
    fetchNav2BehaviorProfiles,
    fetchNav2BehaviorTreeXml,
    fetchNav2Health,
    fetchNav2NavStatus,
    getNav2ParamsApiBaseUrl,
    navServiceDisplayState,
    postNav2BehaviorActivate,
    postNav2NavRestart,
    putNav2BehaviorTreeXml,
    type Nav2BehaviorProfile,
    type NavServiceDisplayState,
} from "lib/nav2ParamsApi";
import useFleetArcId from "lib/utils/useFleetArcId";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";

const MAX_SHOWN_DIFF_LINES = 80;

const DeveloperNavBehaviorTreePanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getNav2ParamsApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [apiReady, setApiReady] = useState(false);
    const [filePath, setFilePath] = useState("");
    const [fileExists, setFileExists] = useState(false);
    const [warning, setWarning] = useState<string | null>(null);
    const [draft, setDraft] = useState("");
    const [savedContent, setSavedContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [navServiceState, setNavServiceState] = useState<NavServiceDisplayState>("unknown");
    const [error, setError] = useState<string | null>(null);
    const [behaviorProfiles, setBehaviorProfiles] = useState<Nav2BehaviorProfile[]>([]);
    const [liveActiveProfile, setLiveActiveProfile] = useState<string | null>(null);
    const [selectedProfile, setSelectedProfile] = useState("");
    const loadToken = useRef(0);
    const baseUrlRef = useRef(baseUrl);
    baseUrlRef.current = baseUrl;

    const dirty = draft !== savedContent;
    const lineDiff = useMemo(
        () => (dirty ? computeLineDiff(savedContent, draft) : []),
        [dirty, draft, savedContent]
    );
    const visibleLineDiff = useMemo(
        () => lineDiff.slice(0, MAX_SHOWN_DIFF_LINES),
        [lineDiff]
    );
    const hiddenDiffCount = lineDiff.length - visibleLineDiff.length;

    const refreshNavStatus = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        try {
            const status = await fetchNav2NavStatus(url);
            setNavServiceState(navServiceDisplayState(status));
        } catch {
            setNavServiceState((prev) => (prev === "unknown" ? "unknown" : prev));
        }
    }, []);

    const reloadXml = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        const token = ++loadToken.current;
        setLoading(true);
        setError(null);
        try {
            const res = await fetchNav2BehaviorTreeXml(url);
            if (token !== loadToken.current) return;
            setFilePath(res.path);
            setFileExists(res.exists);
            if (res.behavior_profile_name) {
                setLiveActiveProfile(res.behavior_profile_name);
            }
            setWarning(res.warning ?? null);
            setDraft(res.content);
            setSavedContent(res.content);
        } catch (e) {
            if (token !== loadToken.current) return;
            setError(e instanceof Error ? e.message : "Failed to load behavior tree XML");
        } finally {
            if (token === loadToken.current) setLoading(false);
        }
    }, []);

    const reloadProfiles = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        try {
            const list = await fetchNav2BehaviorProfiles(url);
            setBehaviorProfiles(list.profiles);
            setLiveActiveProfile(list.active);
            setSelectedProfile((prev) => {
                if (prev && list.profiles.some((p) => p.name === prev)) {
                    return prev;
                }
                if (list.active && list.profiles.some((p) => p.name === list.active)) {
                    return list.active;
                }
                return list.profiles[0]?.name ?? "";
            });
        } catch {
            setBehaviorProfiles([]);
            setLiveActiveProfile(null);
        }
    }, []);

    const reloadAll = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) {
            setApiReady(false);
            setError(t("developer.btXml.apiUnavailable"));
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await fetchNav2Health(url);
            setApiReady(true);
            await Promise.all([reloadXml(), reloadProfiles()]);
            await refreshNavStatus();
        } catch (e) {
            setApiReady(false);
            const msg = e instanceof Error ? e.message : "Failed to reach Nav2 params API";
            setError(`${msg} · ${t("developer.btXml.apiHint")}`);
        } finally {
            setLoading(false);
        }
    }, [refreshNavStatus, reloadProfiles, reloadXml, t]);

    useEffect(() => {
        if (!baseUrl) {
            setApiReady(false);
            setError(t("developer.btXml.apiUnavailable"));
            return;
        }
        void reloadAll();
    }, [baseUrl, reloadAll, t]);

    useEffect(() => {
        if (!apiReady || !baseUrl || navServiceState !== "starting") return;
        const id = window.setInterval(() => void refreshNavStatus(), 2000);
        return () => window.clearInterval(id);
    }, [apiReady, baseUrl, navServiceState, refreshNavStatus]);

    const handleSave = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || !dirty) return;
        setBusy(true);
        try {
            const res = await putNav2BehaviorTreeXml(url, draft);
            setSavedContent(draft);
            setFileExists(true);
            setFilePath(res.path);
            if (res.behavior_profile_name) {
                setLiveActiveProfile(res.behavior_profile_name);
            }
            setWarning(
                res.settings_json_stale
                    ? t("developer.btXml.settingsJsonStale")
                    : null
            );
            addToast({
                type: "success",
                message: res.message ?? t("developer.btXml.saved"),
            });
            await refreshNavStatus();
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : t("developer.btXml.saveFailed"),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, dirty, draft, refreshNavStatus, t]);

    const handleReload = useCallback(() => {
        if (dirty && !window.confirm(t("developer.btXml.discardConfirm"))) return;
        void reloadXml();
    }, [dirty, reloadXml, t]);

    const handleActivate = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || !selectedProfile) return;
        if (
            dirty &&
            !window.confirm(
                t("navigationBehaviour.profiles.activateDirtyConfirm", {
                    defaultValue:
                        "You have unsaved XML changes. Activate the profile from disk and replace live XML?",
                })
            )
        ) {
            return;
        }
        if (
            !window.confirm(
                t("navigationBehaviour.profiles.activateConfirm", {
                    name: selectedProfile,
                    defaultValue: `Activate behaviour profile '${selectedProfile}'? Regenerates live XML and updates nav2_params.yaml. Navigation is not restarted.`,
                })
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            const res = await postNav2BehaviorActivate(url, selectedProfile);
            setLiveActiveProfile(res.behavior_profile_name ?? selectedProfile);
            setWarning(null);
            await reloadXml();
            await reloadProfiles();
            addToast({
                type: "success",
                message:
                    res.message ??
                    t("navigationBehaviour.profiles.activated", {
                        defaultValue: "Behaviour profile activated",
                    }),
            });
            await refreshNavStatus();
        } catch (e) {
            addToast({
                type: "error",
                message:
                    e instanceof Error
                        ? e.message
                        : t("navigationBehaviour.profiles.activateFailed", {
                              defaultValue: "Failed to activate behaviour profile",
                          }),
            });
        } finally {
            setBusy(false);
        }
    }, [
        addToast,
        dirty,
        refreshNavStatus,
        reloadProfiles,
        reloadXml,
        selectedProfile,
        t,
    ]);

    const handleRestartNav = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        if (!window.confirm(t("developer.btXml.restartConfirm"))) return;
        setBusy(true);
        try {
            await postNav2NavRestart(url);
            setNavServiceState("starting");
            addToast({ type: "success", message: t("developer.btXml.restartScheduled") });
            for (let attempt = 0; attempt < 90; attempt += 1) {
                await new Promise((resolve) => window.setTimeout(resolve, 2000));
                const status = await fetchNav2NavStatus(url);
                const nextState = navServiceDisplayState(status);
                setNavServiceState(nextState);
                if (nextState === "running" || nextState === "stopped") break;
            }
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : t("developer.btXml.restartFailed"),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, t]);

    const navStatusLabel =
        navServiceState === "unknown"
            ? t("developer.btXml.navUnknown")
            : navServiceState === "running"
              ? t("developer.btXml.navRunning")
              : navServiceState === "starting"
                ? t("developer.btXml.navStarting")
                : t("developer.btXml.navStopped");

    const navStatusClassName =
        navServiceState === "running"
            ? "text-primary2"
            : navServiceState === "starting"
              ? "text-warning"
              : "text-black40";

    return (
        <section className="mt-8 max-w-4xl border-t border-black80 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-h5 font-semibold text-primary2">
                        {t("developer.btXml.title")}
                    </h3>
                    <p className="mt-0.5 text-h5 text-black40">{t("developer.btXml.description")}</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-h5 text-black40">nav.service:</span>
                    <span className={`text-h5 font-semibold ${navStatusClassName}`}>
                        {navStatusLabel}
                    </span>
                </div>
            </div>

            {error ? <p className="mt-3 text-h5 text-warning">{error}</p> : null}
            {warning ? (
                <div className="mt-3 rounded-r5 border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                    <p className="text-h5 text-amber-100">{warning}</p>
                </div>
            ) : null}
            {!fileExists && apiReady && !loading ? (
                <div className="mt-3 rounded-r5 border border-black70 bg-black100 px-3 py-2">
                    <p className="text-h5 text-black30">{t("developer.btXml.noFileYet")}</p>
                </div>
            ) : null}
            {apiReady && filePath ? (
                <p className="mt-2 break-all font-mono text-[11px] text-black40">{filePath}</p>
            ) : null}
            <div className="mt-4 flex flex-wrap items-end gap-3">
                <label className="flex min-w-[10rem] flex-1 flex-col gap-1">
                    <span className="text-h5 text-black40">
                        {t("navigationBehaviour.profiles.label", {
                            defaultValue: "Behaviour profile",
                        })}
                    </span>
                    <select
                        className="rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5 text-white disabled:opacity-50"
                        value={selectedProfile}
                        disabled={
                            loading || busy || !apiReady || behaviorProfiles.length === 0
                        }
                        onChange={(e) => setSelectedProfile(e.target.value)}
                    >
                        {behaviorProfiles.length === 0 ? (
                            <option value="">
                                {t("navigationBehaviour.profiles.empty", {
                                    defaultValue: "No profiles",
                                })}
                            </option>
                        ) : (
                            behaviorProfiles.map((p) => (
                                <option key={p.name} value={p.name}>
                                    {p.name}
                                    {p.is_active || p.name === liveActiveProfile
                                        ? ` (${t("navigationBehaviour.profiles.activeMarker", {
                                              defaultValue: "active",
                                          })})`
                                        : ""}
                                </option>
                            ))
                        )}
                    </select>
                </label>
                <button
                    type="button"
                    disabled={
                        loading ||
                        busy ||
                        !baseUrl ||
                        !apiReady ||
                        !selectedProfile
                    }
                    onClick={() => void handleActivate()}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80 disabled:opacity-50"
                >
                    {t("navigationBehaviour.profiles.activate", {
                        defaultValue: "Activate",
                    })}
                </button>
                <button
                    type="button"
                    disabled={loading || busy || !baseUrl || !apiReady}
                    onClick={() => void handleReload()}
                    className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80 disabled:opacity-50"
                >
                    {t("developer.btXml.reload")}
                </button>
            </div>
            <p className="mt-2 text-h5 text-black60">{t("developer.btXml.activateHint")}</p>
            {liveActiveProfile ? (
                <p className="mt-1 text-h5 text-black40">
                    {t("developer.btXml.activeBehaviorProfile", {
                        name: liveActiveProfile,
                    })}
                </p>
            ) : null}

            <textarea
                value={draft}
                disabled={loading || busy || !baseUrl || !apiReady}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                placeholder={t("developer.btXml.placeholder")}
                className="mt-3 h-[min(28rem,50vh)] w-full resize-y rounded-r10 border border-black80 bg-black100 p-3 font-mono text-[12px] leading-relaxed text-white disabled:opacity-50"
            />

            {dirty ? (
                <div className="mt-2 space-y-1">
                    <p className="text-h5 text-warning">
                        {t("developer.btXml.unsaved")}
                        {lineDiff.length > 0
                            ? ` · ${lineDiff.length} line${lineDiff.length === 1 ? "" : "s"}`
                            : ""}
                    </p>
                    {visibleLineDiff.length > 0 ? (
                        <pre className="max-h-48 overflow-auto rounded-r5 border border-black80 bg-black100 p-2 font-mono text-[11px] leading-snug text-black20">
                            {visibleLineDiff.map((entry, idx) => (
                                <div
                                    key={`${entry.type}-${entry.line}-${idx}`}
                                    className={
                                        entry.type === "add" ? "text-primary2" : "text-warning"
                                    }
                                >
                                    <span className="inline-block w-10 shrink-0 text-black40">
                                        L{entry.line}
                                    </span>
                                    <span className="mr-1">{entry.type === "add" ? "+" : "−"}</span>
                                    <span className="whitespace-pre-wrap break-all">
                                        {entry.text || " "}
                                    </span>
                                </div>
                            ))}
                            {hiddenDiffCount > 0 ? (
                                <div className="mt-1 text-black40">
                                    {t("developer.btXml.moreDiffLines", {
                                        count: hiddenDiffCount,
                                    })}
                                </div>
                            ) : null}
                        </pre>
                    ) : null}
                </div>
            ) : null}

            <div className="mt-3 flex flex-wrap gap-2">
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading || !dirty}
                    onClick={() => void handleSave()}
                    className="rounded-r5 border border-primary2 bg-primary2 px-3 py-1 text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                >
                    {t("developer.btXml.save")}
                </button>
                <button
                    type="button"
                    disabled={!baseUrl || !apiReady || busy || loading}
                    onClick={() => void handleRestartNav()}
                    className="ml-auto rounded-r5 border border-warning bg-black90 px-3 py-1 text-h5 font-semibold text-warning hover:bg-black80 disabled:opacity-50"
                >
                    {t("developer.btXml.restartNav")}
                </button>
            </div>
            <p className="mt-2 text-h5 text-black60">{t("developer.btXml.restartHint")}</p>
        </section>
    );
};

export default DeveloperNavBehaviorTreePanel;
